import update from "immutability-helper";
import {
  append,
  clone,
  isEmpty,
  isNil,
  keys,
  mergeRight,
  omit,
  partialRight,
  pick,
  pipe,
} from "ramda";
import { sortMedia } from "util/sorts";
import {
  validateField,
  validateModule,
} from "manage-modules/redux/moduleValidation";
import * as actionTypes from "manage-modules/redux/manageModulesActionTypes";

const DRAWER_ITEM_LIMIT = 100;

const initialState = {
  modulesLoaded: false,
  moduleCard: {
    activeTabIndex: 0,
    module: {
      mediaType: "module",
    },
    fields: {
      description: "",
      displayName: "",
      defaultCurationName: "",
      excludeFromCatalog: false,
      iconUrl: "",
      internalCode: "",
      name: "",
      status: "ACTIVE",
      inDplaCatalog: false,
    },
    items: [],
    itemsLoaded: true,
    itemsSynced: true,
    selectedItemIds: [],
    showEditThumbnail: false,
    removeItemsConfirmationDialog: {
      active: false,
    },
    discardNewModuleConfirmationDialog: {
      active: false,
    },
    errors: {},
    fetchingInternalCodeSuggestion: false,
    fetchingBase64EncodedThumbnailUrl: false,
  },
  publishingNewModule: false,
  modulesDrawer: {
    filterQuery: "",
    itemCount: 0,
    itemLimit: DRAWER_ITEM_LIMIT,
    modules: [],
    selectedModuleId: null,
    visibleModules: [],
  },
  syncingFields: {},
};

const addModuleCardErrors = errors => state =>
  update(state, {
    moduleCard: {
      errors: { $merge: errors },
    },
  });

const addSyncingField = (moduleId, field, value) => state => {
  const newModuleSyncingFields = clone(state.syncingFields[moduleId] || {});
  newModuleSyncingFields[field] = value;

  return update(state, {
    syncingFields: {
      [moduleId]: {
        $set: newModuleSyncingFields,
      },
    },
  });
};

const applyFilterQuery = state => {
  const { filterQuery, modules } = state.modulesDrawer;

  const query = filterQuery.toLowerCase();

  const visibleModules = modules.filter(module => {
    const name = module.name.toLowerCase();
    const displayName = module.displayName
      ? module.displayName.toLowerCase()
      : "";
    return name.includes(query) || displayName.includes(query);
  });

  return update(state, {
    modulesDrawer: {
      itemCount: { $set: visibleModules.length },
      itemLimit: { $set: DRAWER_ITEM_LIMIT },
      visibleModules: {
        $set: visibleModules.slice(0, DRAWER_ITEM_LIMIT),
      },
    },
  });
};

const removeModuleCardFieldErrors = field => state => {
  const { errors } = state.moduleCard;

  return update(state, {
    moduleCard: {
      errors: { $set: omit([field], errors) },
    },
  });
};

const removeSyncingField = (moduleId, field) => state => {
  const newSyncingFields = omit([field], state.syncingFields[moduleId]);
  const updateSpec = {};

  if (isEmpty(newSyncingFields)) {
    updateSpec.syncingFields = {
      $set: omit([moduleId], state.syncingFields),
    };
  } else {
    updateSpec.syncingFields = {
      [moduleId]: { $set: newSyncingFields },
    };
  }

  return update(state, updateSpec);
};

const setModuleCardField = (field, value) => state =>
  update(state, {
    moduleCard: {
      fields: {
        [field]: { $set: value },
      },
    },
  });

const setModuleCardModuleField = (field, value) => state =>
  update(state, {
    moduleCard: {
      module: {
        [field]: { $set: value },
      },
    },
  });

const setModuleListModuleField = (moduleId, field, value) => state => {
  const { modules, visibleModules } = state.modulesDrawer;
  const modulesIndex = modules.findIndex(m => moduleId === m.id);
  const visibleModulesIndex = visibleModules.findIndex(m => moduleId === m.id);

  const updates = [];

  updates.push(
    partialRight(update, [
      {
        modulesDrawer: {
          modules: {
            [modulesIndex]: {
              $merge: {
                [field]: value,
              },
            },
          },
        },
      },
    ]),
  );

  if (visibleModulesIndex > -1) {
    updates.push(
      partialRight(update, [
        {
          modulesDrawer: {
            visibleModules: {
              [visibleModulesIndex]: {
                $merge: {
                  [field]: value,
                },
              },
            },
          },
        },
      ]),
    );
  }

  return pipe(...updates)(state);
};

const setModuleCardErrors = errors => state =>
  update(state, {
    moduleCard: {
      errors: { $set: errors },
    },
  });

const setFetchingInternalCodeSuggestion = fetching => state =>
  update(state, {
    moduleCard: {
      fetchingInternalCodeSuggestion: { $set: fetching },
    },
  });

const setFetchingBase64EncodedThumbnailUrl = fetching => state =>
  update(state, {
    moduleCard: {
      fetchingBase64EncodedThumbnailUrl: { $set: fetching },
    },
  });

const validateModuleCardField = field => state => {
  const { fields } = state.moduleCard;
  const errors = validateField(field, fields[field]);

  if (isEmpty(errors)) {
    return removeModuleCardFieldErrors(field)(state);
  } else {
    return addModuleCardErrors(errors)(state);
  }
};

const syncModuleCardField = (field, value) => state => {
  const nextState = pipe(
    setModuleCardField(field, value),
    validateModuleCardField(field),
  )(state);

  const { fields, module } = nextState.moduleCard;

  if (
    fields[field] !== module[field] &&
    isNil(nextState.moduleCard.errors[field])
  ) {
    return pipe(
      setModuleCardModuleField(field, value),
      setModuleListModuleField(module.id, field, value),
      addSyncingField(module.id, field, value),
    )(nextState);
  } else {
    return nextState;
  }
};

const manageModulesReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.APPLY_FILTER_QUERY:
      return applyFilterQuery(state);

    case actionTypes.CHANGE_MODULE_CARD_FIELD:
      return setModuleCardField(action.field, action.value)(state);

    case actionTypes.CHANGE_NEW_MODULE_CARD_THUMBNAIL:
      return setFetchingBase64EncodedThumbnailUrl(true)(state);

    case actionTypes.FETCH_MODULES:
      return clone(initialState);

    case actionTypes.FILTER_QUERY_CHANGED:
      return update(state, {
        modulesDrawer: {
          filterQuery: { $set: action.filterQuery },
        },
      });

    case actionTypes.HIDE_DISCARD_NEW_MODULE_CONFIRMATION_DIALOG:
      return update(state, {
        moduleCard: {
          discardNewModuleConfirmationDialog: {
            active: { $set: false },
          },
        },
      });

    case actionTypes.HIDE_REMOVE_MODULE_ITEMS_CONFIRMATION_DIALOG:
      return update(state, {
        moduleCard: {
          removeItemsConfirmationDialog: {
            active: { $set: false },
          },
        },
      });

    case actionTypes.MODULE_FIELD_SYNCED: {
      const { moduleId, field, value } = action;
      const { moduleCard } = state;
      const updates = [];

      if (moduleCard.module.id === moduleId) {
        updates.push(setModuleCardField(field, value));
        updates.push(setModuleCardModuleField(field, value));
      }

      updates.push(setModuleListModuleField(moduleId, field, value));
      updates.push(removeSyncingField(moduleId, field));

      return pipe(...updates)(state);
    }

    case actionTypes.MODULE_ITEMS_LOADED:
      return update(state, {
        moduleCard: {
          items: { $set: clone(action.items) },
          itemsLoaded: { $set: true },
        },
      });

    case actionTypes.MODULE_ITEMS_SYNCED:
      return update(state, {
        moduleCard: {
          itemsSynced: { $set: true },
        },
      });

    case actionTypes.MODULES_LOADED: {
      const modules = sortMedia(action.modules);

      return update(initialState, {
        modulesLoaded: { $set: true },
        moduleCard: {
          module: { $set: modules.length ? clone(modules[0]) : {} },
        },
        modulesDrawer: {
          filterQuery: { $set: "" },
          itemCount: { $set: modules.length },
          itemLimit: { $set: DRAWER_ITEM_LIMIT },
          modules: { $set: modules },
          selectedModuleId: { $set: null },
          visibleModules: { $set: modules.slice(0, DRAWER_ITEM_LIMIT) },
        },
      });
    }

    case actionTypes.MODULE_THUMBNAIL_SYNCED:
      return removeSyncingField(action.moduleId, "iconUrl")(state);

    case actionTypes.NEW_MODULE:
      return update(state, {
        moduleCard: { $set: clone(initialState.moduleCard) },
      });

    case actionTypes.NEW_MODULE_PUBLISHED:
      return update(state, {
        syncingFields: {
          $set: omit([action.moduleId], state.syncingFields),
        },
        publishingNewModule: { $set: false },
      });

    case actionTypes.NEW_MODULE_CARD_INTERNAL_CODE_SUGGESTED: {
      const { suggestedInternalCode } = action;
      return pipe(
        removeModuleCardFieldErrors("internalCode"),
        setModuleCardField("internalCode", suggestedInternalCode),
        setModuleCardModuleField("internalCode", suggestedInternalCode),
        setFetchingInternalCodeSuggestion(false),
      )(state);
    }

    case actionTypes.PUBLISH_NEW_MODULE: {
      const { moduleCard } = state;
      const errors = validateModule(moduleCard.module);

      if (errors) {
        return setModuleCardErrors(errors)(state);
      } else if (
        !moduleCard.fetchingInternalCodeSuggestion &&
        !moduleCard.fetchingBase64EncodedThumbnailUrl
      ) {
        const { id } = action;
        const newModule = clone(moduleCard.module);
        newModule.id = id;

        const { modulesDrawer } = state;

        return pipe(
          partialRight(update, [
            {
              moduleCard: {
                errors: { $set: {} },
                fetchingInternalCodeSuggestion: { $set: false },
                items: { $set: [] },
                itemsLoaded: { $set: true },
                itemsSynced: { $set: true },
                lastUserNewModuleInternalCodeSuggestion: { $set: "" },
                module: { $set: newModule },
                selectedItemIds: { $set: [] },
              },
              modulesDrawer: {
                modules: {
                  $set: sortMedia(append(newModule, modulesDrawer.modules)),
                },
                selectedModuleId: { $set: newModule.id },
              },
              publishingNewModule: { $set: true },
              syncingFields: {
                [newModule.id]: { $set: clone(newModule) },
              },
            },
          ]),
          applyFilterQuery,
        )(state);
      } else {
        return state;
      }
    }

    case actionTypes.REMOVE_MODULE_CARD_ITEMS: {
      const nextItems = state.moduleCard.items.filter(
        item => !state.moduleCard.selectedItemIds.includes(item.id),
      );

      return update(state, {
        moduleCard: {
          items: { $set: nextItems },
          selectedItemIds: { $set: [] },
          itemsSynced: { $set: false },
          removeItemsConfirmationDialog: {
            active: { $set: false },
          },
        },
      });
    }

    case actionTypes.SAVE_NEW_MODULE_CARD_FIELD: {
      const { field, value } = action;

      const nextState = pipe(
        setModuleCardField(field, value),
        validateModuleCardField(field),
      )(state);

      if (isNil(nextState.moduleCard.errors[field])) {
        return setModuleCardModuleField(field, value)(nextState);
      } else {
        return nextState;
      }
    }

    case actionTypes.SAVE_NEW_MODULE_CARD_FIELD_SUGGEST_AS_INTERNAL_CODE: {
      const { field, value } = action;

      const nextState = pipe(
        setModuleCardField(field, value),
        validateModuleCardField(field),
      )(state);

      const { fields, module } = nextState.moduleCard;

      if (
        fields[field] !== module[field] &&
        isNil(nextState.moduleCard.errors[field])
      ) {
        return pipe(
          setModuleCardModuleField(field, value),
          setFetchingInternalCodeSuggestion(true),
        )(nextState);
      } else {
        return nextState;
      }
    }

    case actionTypes.SELECT_ALL_MODULE_ITEMS:
      return update(state, {
        moduleCard: {
          selectedItemIds: {
            $set: action.selected
              ? state.moduleCard.items.map(item => item.id)
              : [],
          },
        },
      });

    case actionTypes.SELECT_MODULE: {
      const module = state.modulesDrawer.visibleModules.find(
        module => module.id === action.moduleId,
      );

      const newFields = pick(keys(initialState.moduleCard.fields), module);

      return update(state, {
        modulesDrawer: {
          selectedModuleId: { $set: action.moduleId },
        },
        moduleCard: {
          errors: { $set: {} },
          fetchingInternalCodeSuggestion: { $set: false },
          fields: {
            $set: mergeRight(initialState.moduleCard.fields, newFields),
          },
          items: { $set: [] },
          itemsLoaded: { $set: false },
          lastUserNewModuleInternalCodeSuggestion: { $set: "" },
          module: { $set: clone(module) },
          selectedItemIds: { $set: [] },
        },
      });
    }

    case actionTypes.SELECT_MODULE_ITEM: {
      if (action.selected) {
        return update(state, {
          moduleCard: {
            selectedItemIds: { $push: [action.mediaId] },
          },
        });
      } else {
        const selectedItemIds = state.moduleCard.selectedItemIds;

        return update(state, {
          moduleCard: {
            selectedItemIds: {
              $set: selectedItemIds.filter(id => id !== action.mediaId),
            },
          },
        });
      }
    }

    case actionTypes.SHOW_DISCARD_NEW_MODULE_CONFIRMATION_DIALOG:
      return update(state, {
        moduleCard: {
          discardNewModuleConfirmationDialog: {
            active: { $set: true },
          },
        },
      });

    case actionTypes.SHOW_EDIT_MODULE_CARD_THUMBNAIL:
      return update(state, {
        moduleCard: {
          showEditThumbnail: { $set: action.show },
        },
      });

    case actionTypes.SHOW_REMOVE_MODULE_ITEMS_CONFIRMATION_DIALOG:
      return update(state, {
        moduleCard: {
          removeItemsConfirmationDialog: {
            active: { $set: true },
          },
        },
      });

    case actionTypes.SHOW_MODULE_THUMBNAIL_URL: {
      const { moduleId, url } = action;
      const { moduleCard } = state;

      const updates = [];

      updates.push(setFetchingBase64EncodedThumbnailUrl(false));

      // the url of the module in the drawer will be a local file url, but this should be fine
      updates.push(setModuleListModuleField(moduleId, "iconUrl", url));

      if (moduleCard.module.id === moduleId) {
        updates.push(setModuleCardField("iconUrl", url));
      }

      return pipe(...updates)(state);
    }

    case actionTypes.SHOW_NEW_MODULE_THUMBNAIL_URL:
      return pipe(
        removeModuleCardFieldErrors("iconUrl"),
        setFetchingBase64EncodedThumbnailUrl(false),
        setModuleCardField("iconUrl", action.url),
        setModuleCardModuleField("iconUrl", action.url),
      )(state);

    case actionTypes.SHOW_MORE_MODULES: {
      const { modules, itemLimit } = state.modulesDrawer;
      const newItemLimit = itemLimit + DRAWER_ITEM_LIMIT;

      return update(state, {
        modulesDrawer: {
          itemLimit: {
            $set: newItemLimit,
          },
          visibleModules: {
            $set: modules.slice(0, newItemLimit),
          },
        },
      });
    }

    case actionTypes.SYNC_MODULE_CARD_FIELD:
      return syncModuleCardField(action.field, action.value)(state);

    case actionTypes.SYNC_MODULE_CARD_INTERNAL_CODE:
      return syncModuleCardField("internalCode", action.internalCode)(state);

    case actionTypes.SYNC_MODULE_CARD_THUMBNAIL: {
      const { module } = state.moduleCard;

      return pipe(
        setFetchingBase64EncodedThumbnailUrl(true),
        addSyncingField(module.id, "iconUrl", module.iconUrl),
      )(state);
    }

    case actionTypes.TAB_CHANGED:
      return update(state, {
        moduleCard: {
          activeTabIndex: { $set: action.index },
        },
      });

    default:
      return state;
  }
};

export default manageModulesReducer;
